export type IsoDateTime = string

export function nowIso(): IsoDateTime {
  return new Date().toISOString()
}

export function addSeconds(iso: IsoDateTime, seconds: number): IsoDateTime {
  return new Date(new Date(iso).getTime() + seconds * 1000).toISOString()
}

export function addDays(iso: IsoDateTime, days: number): IsoDateTime {
  return addSeconds(iso, days * 86400)
}

export function isPast(iso: IsoDateTime | null | undefined, now: IsoDateTime = nowIso()): boolean {
  if (!iso) return false
  return new Date(iso).getTime() < new Date(now).getTime()
}

export function isBetween(
  now: IsoDateTime,
  start: IsoDateTime | null | undefined,
  end: IsoDateTime | null | undefined,
): boolean {
  const t = new Date(now).getTime()
  if (start && t < new Date(start).getTime()) return false
  if (end && t > new Date(end).getTime()) return false
  return true
}

/** Formats an ISO timestamp in the organisation's timezone. */
export function formatDateTime(
  iso: IsoDateTime,
  opts: Intl.DateTimeFormatOptions & { timeZone?: string } = {},
): string {
  return new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: 'America/Chicago',
    ...opts,
  }).format(new Date(iso))
}

const SITE_TIME_ZONE = 'America/Chicago'

/** Minutes `timeZone` is ahead of UTC at instant `ts`. */
function zoneOffsetMinutes(ts: number, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(new Date(ts))
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? 0)
  const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'))
  return Math.round((asUtc - Math.floor(ts / 60000) * 60000) / 60000)
}

/** A wall-clock date ("2026-10-10") and time ("09:00") in `timeZone` → ISO UTC. */
export function zonedToIso(date: string, time: string, timeZone = SITE_TIME_ZONE): IsoDateTime {
  const [y, mo, d] = date.split('-').map(Number)
  const [h, mi] = time.split(':').map(Number)
  const guess = Date.UTC(y ?? 0, (mo ?? 1) - 1, d ?? 1, h ?? 0, mi ?? 0)
  let utc = guess - zoneOffsetMinutes(guess, timeZone) * 60000
  utc = guess - zoneOffsetMinutes(utc, timeZone) * 60000
  return new Date(utc).toISOString()
}

/** ISO → wall-clock { date: "YYYY-MM-DD", time: "HH:MM" } in `timeZone`. */
export function isoToZoned(iso: IsoDateTime, timeZone = SITE_TIME_ZONE) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(new Date(iso))
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '00'
  return {
    date: `${get('year')}-${get('month')}-${get('day')}`,
    time: `${get('hour')}:${get('minute')}`,
  }
}

/** The calendar year at the site (Central time) at an instant; workers run on UTC. */
export function siteYear(iso: IsoDateTime = nowIso()): number {
  return Number(isoToZoned(iso).date.slice(0, 4))
}

/** A site calendar year as a UTC range, `start` inclusive and `end` exclusive. */
export function siteYearRange(year: number): { start: IsoDateTime; end: IsoDateTime } {
  return {
    start: zonedToIso(`${year}-01-01`, '00:00'),
    end: zonedToIso(`${year + 1}-01-01`, '00:00'),
  }
}

/** The last millisecond of a site calendar day ("2026-10-03"), as ISO UTC. */
export function siteDayEnd(date: string): IsoDateTime {
  return new Date(Date.parse(zonedToIso(date, '23:59')) + 59_999).toISOString()
}
